import type { z } from "zod";
import { fetchFastApi } from "../api/fastapi";

/**
 * How a poll ended. `failed` means the status endpoint stopped answering
 * usefully, not that the job failed: a job that reports its own failure comes
 * back as `done` with that status. `cancelled` means the caller went away
 * (`isCancelled`), and wins over `timed_out` and `failed`.
 */
export type PollJobResult<T> =
  | { kind: "done"; status: T }
  | { kind: "timed_out" }
  | { kind: "failed"; error: unknown }
  | { kind: "cancelled" };

interface PollJobOptions<Schema extends z.ZodTypeAny> {
  path: string;
  schema: Schema;
  isTerminal: (status: z.infer<Schema>) => boolean;
  // A fixed cadence, or one picked from the time since polling began.
  intervalMs: number | ((elapsedMs: number) => number);
  // Back a failed poll off from the cadence, doubling per failure up to
  // MAX_BACKOFF_MS. Off by default, so a failed poll is retried on the cadence.
  backoffOnFailure?: boolean;
  deadlineMs: number;
  // A request that throws, or answers a body `schema` rejects, is a failed
  // poll; this many in a row ends the wait. Any good answer resets the count.
  // Defaults to MAX_CONSECUTIVE_POLL_FAILURES.
  maxConsecutiveFailures?: number;
  // Checked before each request and before reporting `timed_out` or `failed`.
  // A response already on its way when the caller leaves is still delivered.
  // Omitted, the poll runs until the job ends.
  isCancelled?: () => boolean;
}

// A few dropped polls say nothing about the job; a sustained run of them means
// the status endpoint is not coming back.
export const MAX_CONSECUTIVE_POLL_FAILURES = 5;

// The repo's other pollers cap their failure backoff here too.
const MAX_BACKOFF_MS = 30000;

const neverCancelled = () => false;

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Poll a background job's status endpoint until it reports a terminal status.
 * Asks immediately, then waits `intervalMs` between polls. Never rejects: every
 * ending, including a caller that went away, is a `PollJobResult`.
 *
 * Every failed poll is retried. `pollEvidenceRun` keeps its own loop because it
 * retries only transient errors.
 */
export async function pollJob<Schema extends z.ZodTypeAny>({
  path,
  schema,
  isTerminal,
  intervalMs,
  deadlineMs,
  backoffOnFailure = false,
  maxConsecutiveFailures = MAX_CONSECUTIVE_POLL_FAILURES,
  isCancelled = neverCancelled,
}: PollJobOptions<Schema>): Promise<PollJobResult<z.infer<Schema>>> {
  const startedAt = Date.now();
  let consecutiveFailures = 0;

  while (Date.now() - startedAt < deadlineMs) {
    if (isCancelled()) return { kind: "cancelled" };

    try {
      const response = await fetchFastApi({
        path,
        method: "GET",
        throwOnErrorEnvelope: true,
      });
      const status: z.infer<Schema> = schema.parse(response);
      consecutiveFailures = 0;
      if (isTerminal(status)) return { kind: "done", status };
    } catch (error) {
      console.error(`Error polling job status at ${path}:`, error);
      consecutiveFailures += 1;
      if (consecutiveFailures >= maxConsecutiveFailures) {
        if (isCancelled()) return { kind: "cancelled" };
        return { kind: "failed", error };
      }
    }

    const elapsedMs = Date.now() - startedAt;
    const baseMs =
      typeof intervalMs === "number" ? intervalMs : intervalMs(elapsedMs);
    const waitMs = backoffOnFailure
      ? Math.min(
          baseMs * 2 ** consecutiveFailures,
          Math.max(baseMs, MAX_BACKOFF_MS),
        )
      : baseMs;
    // Never sleep past the deadline, so the last poll still happens.
    await sleep(Math.min(waitMs, Math.max(deadlineMs - elapsedMs, 0)));
  }

  if (isCancelled()) return { kind: "cancelled" };
  return { kind: "timed_out" };
}
